// src/components/pages/allocation/partial/BacktestResult.js

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import PerformanceTable from "./PerformanceTable"; // ✅ 테이블을 가져옴
import DataPeriodAlert from "../../backtest/partials/DataPeriodAlert"
import commonStyles from "../../../../styles/Common.module.css";
import { useTranslation } from "react-i18next"; // react-i18next 훅 임포트

function BacktestResult({ 
  stocks, 
  period = 10, 
  benchmark = "SPY", 
  optimizedWeights,
  customStart,
  customEnd 
}) {
  const [backtestData, setBacktestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation(); // useTranslation 호출 추가

  // console.log("📌 [BacktestResult.js] 컴포넌트 렌더링됨!");
  // console.log("✅ 현재 stocks 상태:", stocks);
  console.log("✅ [BacktestResult.js] 현재 optimizedWeights 상태:", optimizedWeights);

  useEffect(() => {
    if (!stocks || stocks.length === 0) {
      console.log("❌ [BacktestResult.js] stocks가 비어있음. 백테스트 실행 안 함.");
      return;
    }

    if (!optimizedWeights || Object.keys(optimizedWeights).length === 0) {
      console.log("❌ [BacktestResult.js] optimizedWeights가 비어있음. 백테스트 실행 안 함.");
      return;
    }

    const fetchData = async () => {
      console.log("📌 [BacktestResult.js] 백테스트 API 호출 시작");
      setLoading(true);
      setError(null);
      try {
        let url = `/.netlify/functions/backtest?stocks=${stocks.join(",")}&benchmark=${encodeURIComponent(benchmark)}&weights=${encodeURIComponent(JSON.stringify(optimizedWeights))}`;
        
        // ✨ 2. 커스텀 기간 여부에 따라 URL을 조건부로 구성
        if (customStart && customEnd) {
            // 커스텀 기간이 유효하면 'start'와 'end' 파라미터를 사용
            url += `&b_start=${customStart}&b_end=${customEnd}`;
            console.log("API URL (커스텀 기간):", url);
        } else {
            // 커스텀 기간이 없으면 기존 'period' 파라미터를 사용
            url += `&b_period=${period}`;
            console.log("API URL (년 단위 기간):", url);
        }

        const response = await fetch(url);
        
        if (!response.ok) {
          // 응답 본문을 읽습니다. (오류 상태일지라도 메시지를 얻기 위함)
          // JSON 파싱 실패 시 빈 객체로 폴백
          const resultData = await response.json().catch(() => ({})); 
          let errorMessage;
          
          // 400 Bad Request 처리
          if (response.status === 400) {
            // 'invalidStocks'가 있는 경우 (데이터 부족 오류)
            if (resultData.invalidStocks) {
              const invalidTickers = resultData.invalidStocks.join(', ');
              // start/end가 있을 때와 없을 때 메시지를 분리
              const dateParams = customStart && customEnd ? { customStart, customEnd } : {};
              errorMessage = t("backtest.error.dataMissingStrict2", { 
                  tickers: invalidTickers, 
                  customStart: dateParams.customStart || 'N/A', 
                  customEnd: dateParams.customEnd || 'N/A'
              });
            } 
            // 'No stocks provided' (사용자 문의 오류)나 기타 400 오류
            else {
              // 'no stocks provided'가 resultData.error에 담겨 옵니다.
              errorMessage = resultData.error || t("backtest.fetchErrorDetailed");
            }
          } 
          // 500 Internal Server Error 처리
          else if (response.status === 500) {
              errorMessage = t("backtest.serverError") || "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
          }
          // 기타 오류
          else {
              errorMessage = t("backtest.fetchErrorDetailed") || `오류 코드 ${response.status} 발생.`;
          }
          
          // 상세 오류 메시지를 catch 블록으로 던집니다.
          throw new Error(errorMessage); 
        }
        const data = await response.json();
        // console.log("📌 [BacktestResult.js] 백테스트 API 응답:", data);
        setBacktestData(data);

      } catch (err) {
        console.error("❌ [BacktestResult.js] 백테스트 API 요청 실패:", err.message);
        setError(err.message);
      }
      setLoading(false);
    };

    fetchData();
  }, [stocks, optimizedWeights, period, benchmark, customStart, customEnd]);


  if (!stocks || stocks.length === 0) {
    return <div>`${t("noPortfolio")}`</div>;   
  }
  
  if (loading) return ( // 👈 소괄호로 묶어서 ASI를 방지
    <div className={commonStyles.loadingContainer}>
      <div className={commonStyles.spinner}></div>
      {t("loadingData")}
    </div>
  ); 
  if (error) return <div className="error-message"> {error}</div>;
  if (!backtestData || !backtestData.portfolio || !backtestData.benchmark || !backtestData.backtestPeriod) {
  return <div className="error-message">`${t("fetchError")}`</div>;
}


const { portfolio, benchmark: bm, backtestPeriod } = backtestData;

  // ✅ 최적화된 포트폴리오 및 벤치마크 성과 지표
  // ✅ Equal Weighted & Optimized 포트폴리오 성과 지표
  const equalPortfolio = portfolio.equalWeighted || null;
  const optimizedPortfolio = portfolio.optimized || null;
  const benchmarkData = bm || null;

  // ✅ 성과 지표 할당
  const equalMetrics = equalPortfolio?.metrics || {};
  const optimizedMetrics = optimizedPortfolio?.metrics || {};
  const benchmarkMetrics = benchmarkData?.metrics || {};

  // ✅ `dates`가 없으면 오류 방지
  if (!equalPortfolio || !equalPortfolio.dates || !equalPortfolio.cumulative) {
    console.error("❌ [BacktestResult.js] Equal Weighted 포트폴리오 데이터 없음:", equalPortfolio);
    return <div>`${t("equalPortfolioError")}`</div>;
  }
  if (!optimizedPortfolio || !optimizedPortfolio.dates || !optimizedPortfolio.cumulative) {
    console.error("❌ [BacktestResult.js] 최적화된 포트폴리오 데이터 없음:", optimizedPortfolio);
    return <div>`${t("ptimizedPortfolioError")}`</div>;
  }


  return (
    <div>
      {/* 실제 사용된 백테스트 기간 표시 */}
      <span className={commonStyles["data-period-tooltip"]}>
        ℹ️
        <span className={commonStyles["tooltip-text"]}>
            {t("BacktestTooltip")}
        </span>
      </span>
      <h3>{t("cumulativeReturn")}</h3>
      <p>
        {backtestPeriod.start}~{backtestPeriod.end}&nbsp;
        ({backtestPeriod.days} days)
      </p>
      {backtestData.dataPeriodInfo && backtestData.dataPeriodInfo.isPeriodLimited && (
        <DataPeriodAlert 
            dataPeriodInfo={backtestData.dataPeriodInfo} 
            t={t}
        />
      )}
            
      <Plot
        data={[
          {
            x: equalPortfolio.dates,
            y: equalPortfolio.cumulative,
            type: "scatter",
            mode: "lines",
            name: `${t("equalAllocation")}`,
          },
          {
            x: optimizedPortfolio.dates,
            y: optimizedPortfolio.cumulative,
            type: "scatter",
            mode: "lines",
            name: `${t("optimizedPortfolio")}`,
          },
        ]}
        layout={{
          title: "포트폴리오 vs 벤치마크 수익률 변화",
          xaxis: { title: "날짜" },
          yaxis: { title: `${t("cumulativeReturn")}` },
          autosize: true,
          responsive: true,
          margin: { l: 50, r: 50, t: 50, b: 80 },
          legend: {
            orientation: "h",
            x: 0.5,
            xanchor: "center",
            y: -0.3,
          },
          dragmode: false,
        }}
        className="plot-container"
        useResizeHandler={true}
        style={{ width: "100%", height: "100%" }}
        config={{
          displayModeBar: false,
        }}
      />

      <h3>{t("analysisPastDataBased")}</h3>
      {/* ✅ PerformanceTable을 가져와서 사용! */}
      <PerformanceTable equalMetrics={equalMetrics} optimizedMetrics={optimizedMetrics} benchmarkMetrics={benchmarkMetrics} benchmarkSymbol={bm.symbol}/>
    

    </div>
    
  );
}

export default BacktestResult;